import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { calculateRowLabelPositions } from './planUtils';

export const isStageArea = (area) => {
  if (!area) return false;
  const text = (area.text?.text || '').trim().toLowerCase();
  const name = (area.name || '').trim().toLowerCase();
  const label = (area.label || '').trim().toLowerCase();
  return text.includes('stage') || name.includes('stage') || label.includes('stage');
};

export function Canvas({
  plan,
  activeZoneIndex = 0,
  activeTool = 'select', // 'select' (V), 'seat-select' (S), 'add-rows' (B), 'add-row' (N), 'add-seat' (M), 'add-rect' (R), 'add-circle' (C), 'add-ellipse' (E), 'add-polygon' (P), 'add-text' (T), 'pan'
  selectedRowIndex = null,
  selectedSeatGuids = [],
  selectedAreaIndices = [],
  onSelectRow,
  onSelectSeats,
  onSelectAreas,
  onMoveRow,
  onTransformGroup,
  onMoveSingleSeat,
  onMoveMultipleSeats,
  onMoveArea,
  onCommitCreatedRow,
  onCommitCreatedRows,
  onCommitCreatedSeat,
  onCommitCreatedArea,
  zoom = 1,
  pan = { x: 0, y: 0 },
  onPanChange,
  onZoomChange,
  snapToGrid = true,
  onFinishDrawing,
  onDragEnd,
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Dragging existing objects
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const dragOriginRef = useRef(null);
  const dragAppliedDeltaRef = useRef({ dx: 0, dy: 0 });

  // Interactive Drawing Draft State
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStartPos, setDrawStartPos] = useState({ x: 0, y: 0 });
  const [drawCurrentPos, setDrawCurrentPos] = useState({ x: 0, y: 0 });

  // Group Rotation State
  const [isRotatingGroup, setIsRotatingGroup] = useState(false);
  const [groupRotateCenter, setGroupRotateCenter] = useState(null);
  const [groupRotateTotalAngle, setGroupRotateTotalAngle] = useState(0);
  const [lastRotateAngle, setLastRotateAngle] = useState(0);

  // Group Resize State
  const [isResizingGroup, setIsResizingGroup] = useState(false);
  const [resizeInitialData, setResizeInitialData] = useState(null);

  useEffect(() => {
    setGroupRotateTotalAngle(0);
    setGroupRotateCenter(null);
    setIsResizingGroup(false);
    setResizeInitialData(null);
  }, [selectedSeatGuids, selectedAreaIndices]);

  // Polygon multi-point draft
  const [polygonDraftPoints, setPolygonDraftPoints] = useState([]);

  // Selection box (Marquee drag select)
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectStartPos, setSelectStartPos] = useState({ x: 0, y: 0 });
  const [selectCurrentPos, setSelectCurrentPos] = useState({ x: 0, y: 0 });

  const canvasBg = useColorModeValue('#E8ECF0', '#111827');
  const gridColor = useColorModeValue(
    'rgba(0,0,0,0.12)',
    'rgba(255,255,255,0.05)',
  );
  const planBorderColor = useColorModeValue(
    'rgba(0,0,0,0.25)',
    'rgba(255,255,255,0.12)',
  );

  const width = plan?.size?.width || 900;
  const height = plan?.size?.height || 900;

  const categoryColorMap = React.useMemo(() => {
    const map = {};
    (plan?.categories || []).forEach((c) => {
      map[c.name] = c.color;
    });
    return map;
  }, [plan?.categories]);

  const zones = plan?.zones || [];
  const currentZone = zones[activeZoneIndex] ||
    zones[0] || { rows: [], areas: [] };
  const isZoneLocked = Boolean(currentZone.is_locked);
  const rows = currentZone.rows || [];
  const areas = currentZone.areas || [];

  // Coordinate conversion helper (NO snap here — snap applied at object position level)
  const getCanvasCoords = useCallback(
    (clientX, clientY) => {
      if (!svgRef.current) return { x: 0, y: 0 };
      const rect = svgRef.current.getBoundingClientRect();
      const x = (clientX - rect.left - pan.x) / zoom;
      const y = (clientY - rect.top - pan.y) / zoom;
      return { x, y };
    },
    [pan, zoom],
  );

  const selectedGroupBoundingBox = React.useMemo(() => {
    if (selectedSeatGuids.length === 0 && selectedAreaIndices.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let parentRows = new Set();
    let parentAreas = new Set();
    const selectedSet = new Set(selectedSeatGuids);
    const selectedAreaSet = new Set(selectedAreaIndices);

    // First pass to get the center if we don't have one
    let cx = groupRotateCenter?.x || 0;
    let cy = groupRotateCenter?.y || 0;

    if (!groupRotateCenter) {
      let tMinX = Infinity, tMinY = Infinity, tMaxX = -Infinity, tMaxY = -Infinity;
      (rows || []).forEach((row) => {
        (row.seats || []).forEach(seat => {
          if (selectedSet.has(seat.seat_guid)) {
            const rRad = (row.rotation || 0) * (Math.PI / 180);
            const absX = (row.position?.x || 0) + ((seat.position?.x || 0) * Math.cos(rRad) - (seat.position?.y || 0) * Math.sin(rRad));
            const absY = (row.position?.y || 0) + ((seat.position?.x || 0) * Math.sin(rRad) + (seat.position?.y || 0) * Math.cos(rRad));
            if (absX < tMinX) tMinX = absX; if (absX > tMaxX) tMaxX = absX;
            if (absY < tMinY) tMinY = absY; if (absY > tMaxY) tMaxY = absY;
          }
        });
      });
      (areas || []).forEach((area, aIdx) => {
        if (selectedAreaSet.has(aIdx)) {
          const ax = area.position?.x || 0;
          const ay = area.position?.y || 0;
          if (ax < tMinX) tMinX = ax; if (ax > tMaxX) tMaxX = ax;
          if (ay < tMinY) tMinY = ay; if (ay > tMaxY) tMaxY = ay;
        }
      });
      cx = (tMinX + tMaxX) / 2;
      cy = (tMinY + tMaxY) / 2;
    }

    const unrotateRad = -groupRotateTotalAngle * (Math.PI / 180);
    const uCos = Math.cos(unrotateRad);
    const uSin = Math.sin(unrotateRad);

    const addPoint = (px, py) => {
      // Un-rotate point around cx, cy
      const dx = px - cx;
      const dy = py - cy;
      const ux = cx + (dx * uCos - dy * uSin);
      const uy = cy + (dx * uSin + dy * uCos);
      if (ux < minX) minX = ux; if (ux > maxX) maxX = ux;
      if (uy < minY) minY = uy; if (uy > maxY) maxY = uy;
    };

    (rows || []).forEach((row, rIdx) => {
      let hasSelectedSeat = false;
      (row.seats || []).forEach(seat => {
        if (selectedSet.has(seat.seat_guid)) {
          hasSelectedSeat = true;
          const rRad = (row.rotation || 0) * (Math.PI / 180);
          const absX = (row.position?.x || 0) + ((seat.position?.x || 0) * Math.cos(rRad) - (seat.position?.y || 0) * Math.sin(rRad));
          const absY = (row.position?.y || 0) + ((seat.position?.x || 0) * Math.sin(rRad) + (seat.position?.y || 0) * Math.cos(rRad));
          addPoint(absX, absY);
        }
      });
      if (hasSelectedSeat) parentRows.add(rIdx);
    });

    (areas || []).forEach((area, aIdx) => {
      if (selectedAreaSet.has(aIdx)) {
        parentAreas.add(aIdx);
        const ax = area.position?.x || 0;
        const ay = area.position?.y || 0;
        const aRad = (area.rotation || 0) * (Math.PI / 180);
        const aCos = Math.cos(aRad); const aSin = Math.sin(aRad);

        if (area.shape === 'polygon' && area.polygon) {
          (area.polygon.points || []).forEach(p => {
            addPoint(ax + p.x * aCos - p.y * aSin, ay + p.x * aSin + p.y * aCos);
          });
        } else {
          let w = 40, h = 40;
          if (area.shape === 'rectangle' && area.rectangle) { w = area.rectangle.width; h = area.rectangle.height; }
          else if (area.shape === 'text' && area.text) {
            w = Math.max(40, (area.text.text?.length || 4) * ((area.text.fontSize || 16) * 0.65) + 12);
            h = (area.text.fontSize || 16) * 1.3 + 8;
          }
          else if (area.shape === 'circle' && area.circle) { w = (area.circle.radius || 30) * 2; h = (area.circle.radius || 30) * 2; }
          else if (area.shape === 'ellipse' && area.ellipse) { w = (area.ellipse.radius?.x || 50) * 2; h = (area.ellipse.radius?.y || 80) * 2; }

          [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]].forEach(([px, py]) => {
            addPoint(ax + px * aCos - py * aSin, ay + px * aSin + py * aCos);
          });
        }
      }
    });

    if (minX === Infinity) return null;

    minX -= 30; minY -= 30; maxX += 30; maxY += 30;

    return {
      minX, minY, maxX, maxY,
      cx, cy,
      width: maxX - minX, height: maxY - minY,
      parentRows: Array.from(parentRows),
      parentAreas: Array.from(parentAreas)
    };
  }, [selectedSeatGuids, selectedAreaIndices, rows, areas, groupRotateTotalAngle, groupRotateCenter]);

  // Wheel handler: two-finger scroll or wheel pans canvas; holding Ctrl / Cmd zooms
  const handleWheel = (e) => {
    e.preventDefault();

    // Zooming requires holding Ctrl or Meta key (e.g. Ctrl + Mouse Wheel, or touchpad pinch gesture which sets ctrlKey)
    const isZoomGesture = e.ctrlKey || e.metaKey;

    if (isZoomGesture) {
      const zoomFactor = 1.08;
      let newZoom = e.deltaY < 0 ? zoom * zoomFactor : zoom / zoomFactor;
      newZoom = Math.min(Math.max(0.15, newZoom), 5);

      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const newPanX = mouseX - (mouseX - pan.x) * (newZoom / zoom);
      const newPanY = mouseY - (mouseY - pan.y) * (newZoom / zoom);

      onZoomChange(newZoom);
      onPanChange({ x: newPanX, y: newPanY });
    } else {
      // Normal two-finger swipe on touchpad or mouse wheel pans the canvas smoothly
      const newPanX = pan.x - e.deltaX;
      const newPanY = pan.y - e.deltaY;
      onPanChange({ x: newPanX, y: newPanY });
    }
  };

  // Handle ESC and Enter for drawing tools
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsDrawing(false);
        setPolygonDraftPoints([]);
        if (onFinishDrawing) onFinishDrawing();
      } else if (e.key === 'Enter') {
        if (activeTool === 'add-polygon' && polygonDraftPoints.length >= 3) {
          // Finalize Polygon with centroid as origin
          let cx = 0, cy = 0;
          polygonDraftPoints.forEach(p => { cx += p.x; cy += p.y; });
          cx /= polygonDraftPoints.length;
          cy /= polygonDraftPoints.length;

          const relPoints = polygonDraftPoints.map(pt => ({ x: pt.x - cx, y: pt.y - cy }));

          onCommitCreatedArea({
            shape: 'polygon',
            uuid: Math.random().toString(36).substring(2, 9),
            position: { x: cx, y: cy },
            rotation: 0,
            color: '#CCCCCC',
            border_color: '#000001',
            text: { text: '', color: '#333333', position: { x: 0, y: 5 } },
            polygon: { points: relPoints },
          });
          setPolygonDraftPoints([]);
          if (onFinishDrawing) onFinishDrawing();
        }
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        if (activeTool === 'add-polygon' && polygonDraftPoints.length > 0) {
          setPolygonDraftPoints(prev => prev.slice(0, -1));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTool, polygonDraftPoints, onCommitCreatedArea, onFinishDrawing]);

  // Mouse Down handler
  const handleMouseDown = (e) => {
    // Pan mode (Middle click, CTRL+click, Space)
    if (e.button === 1 || e.ctrlKey || e.metaKey || activeTool === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }

    if (e.button !== 0 || isZoneLocked) return;

    const coords = getCanvasCoords(e.clientX, e.clientY);

    // Interactive tool dispatch
    if (activeTool === 'add-seat') {
      onCommitCreatedSeat(coords.x, coords.y);
      return;
    }

    if (activeTool === 'add-text') {
      onCommitCreatedArea({
        shape: 'text',
        uuid: Math.random().toString(36).substring(2, 9),
        position: coords,
        rotation: 0,
        text: {
          text: 'Text',
          fontSize: 16,
          color: '#333333',
          position: { x: 0, y: 0 },
        },
      });
      if (onFinishDrawing) onFinishDrawing();
      return;
    }

    if (activeTool === 'add-polygon') {
      if (polygonDraftPoints.length >= 3) {
        const firstPoint = polygonDraftPoints[0];
        const dist = Math.hypot(coords.x - firstPoint.x, coords.y - firstPoint.y);
        if (dist < 15 / zoom) {
          // Finalize Polygon with centroid as origin
          let cx = 0, cy = 0;
          polygonDraftPoints.forEach(p => { cx += p.x; cy += p.y; });
          cx /= polygonDraftPoints.length;
          cy /= polygonDraftPoints.length;

          const relPoints = polygonDraftPoints.map(p => ({ x: p.x - cx, y: p.y - cy }));

          onCommitCreatedArea({
            shape: 'polygon',
            uuid: Math.random().toString(36).substring(2, 9),
            position: { x: cx, y: cy },
            rotation: 0,
            color: '#CCCCCC',
            border_color: '#000001',
            polygon: { points: relPoints },
            text: { text: '', color: '#333333', position: { x: 0, y: 5 } },
          });
          setPolygonDraftPoints([]);
          if (onFinishDrawing) onFinishDrawing();
          return;
        }
      }
      setPolygonDraftPoints((prev) => [...prev, coords]);
      return;
    }

    if (
      ['add-rows', 'add-row', 'add-rect', 'add-circle', 'add-ellipse'].includes(
        activeTool,
      )
    ) {
      setIsDrawing(true);
      setDrawStartPos(coords);
      setDrawCurrentPos(coords);
      return;
    }

    if (['select', 'seat-select'].includes(activeTool)) {
      setIsSelecting(true);
      setSelectStartPos(coords);
      setSelectCurrentPos(coords);
      return;
    }

    // Default background click clears selection
    if (e.target.id === 'canvas-bg' || e.target.tagName === 'svg') {
      onSelectRow(null);
      onSelectSeats([]);
      onSelectAreas([]);
      return;
    }
  };

  const handleStartResize = (e, handle) => {
    e.stopPropagation();
    if (!selectedGroupBoundingBox || isZoneLocked) return;

    const coords = getCanvasCoords(e.clientX, e.clientY);
    const cx = groupRotateCenter?.x || selectedGroupBoundingBox.cx;
    const cy = groupRotateCenter?.y || selectedGroupBoundingBox.cy;

    const initialSeats = {};
    const initialRows = {};
    const initialAreas = {};
    const selectedSet = new Set(selectedSeatGuids);
    const selectedAreaSet = new Set(selectedAreaIndices);

    (rows || []).forEach((row, ri) => {
      let hasSeat = false;
      (row.seats || []).forEach((seat) => {
        if (selectedSet.has(seat.seat_guid)) {
          hasSeat = true;
          const rRad = (row.rotation || 0) * (Math.PI / 180);
          const absX = (row.position?.x || 0) + ((seat.position?.x || 0) * Math.cos(rRad) - (seat.position?.y || 0) * Math.sin(rRad));
          const absY = (row.position?.y || 0) + ((seat.position?.x || 0) * Math.sin(rRad) + (seat.position?.y || 0) * Math.cos(rRad));
          initialSeats[seat.seat_guid] = {
            absX,
            absY,
            radius: seat.radius || row.seat_radius || 10,
            rowIdx: ri,
            localX: seat.position?.x || 0,
            localY: seat.position?.y || 0,
          };
        }
      });
      if (hasSeat) {
        initialRows[ri] = {
          x: row.position?.x || 0,
          y: row.position?.y || 0,
          rotation: row.rotation || 0,
          seat_spacing: row.seat_spacing || 25,
          seat_radius: row.seat_radius || 10,
        };
      }
    });

    (areas || []).forEach((area, ai) => {
      if (selectedAreaSet.has(ai)) {
        initialAreas[ai] = {
          x: area.position?.x || 0,
          y: area.position?.y || 0,
          rectWidth: area.rectangle?.width || 60,
          rectHeight: area.rectangle?.height || 60,
          circleRadius: area.circle?.radius || 30,
          ellipseRadiusX: area.ellipse?.radius?.x || 50,
          ellipseRadiusY: area.ellipse?.radius?.y || 30,
          fontSize: area.text?.fontSize || 16,
          polygonPoints: area.polygon?.points ? area.polygon.points.map(p => ({ ...p })) : null,
        };
      }
    });

    const cornerPos = { x: handle.x, y: handle.y };
    const initialDist = Math.hypot(cornerPos.x - cx, cornerPos.y - cy);

    setResizeInitialData({
      cx,
      cy,
      cornerPos,
      initialDist: Math.max(initialDist, 1),
      initialSeats,
      initialRows,
      initialAreas,
      parentRows: selectedGroupBoundingBox.parentRows,
      parentAreas: selectedGroupBoundingBox.parentAreas,
    });
    setIsResizingGroup(true);
  };

  // Mouse Move handler
  const handleMouseMove = (e) => {
    if (isPanning) {
      onPanChange({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    const coords = getCanvasCoords(e.clientX, e.clientY);

    // Group corner handle resizing
    if (isResizingGroup && resizeInitialData && onTransformGroup) {
      const { cx, cy, cornerPos, initialDist, initialSeats, initialRows, initialAreas, parentRows, parentAreas } = resizeInitialData;

      let mx = coords.x - cx;
      let my = coords.y - cy;
      if (groupRotateTotalAngle !== 0) {
        const rad = -groupRotateTotalAngle * (Math.PI / 180);
        const uCos = Math.cos(rad);
        const uSin = Math.sin(rad);
        const unRotX = mx * uCos - my * uSin;
        const unRotY = mx * uSin + my * uCos;
        mx = unRotX;
        my = unRotY;
      }

      const vx = cornerPos.x - cx;
      const vy = cornerPos.y - cy;

      // Calculate proportional scale along the dominant movement axis
      const dx = mx - vx;
      const dy = my - vy;
      const safeVx = Math.abs(vx) < 5 ? (vx >= 0 ? 5 : -5) : vx;
      const safeVy = Math.abs(vy) < 5 ? (vy >= 0 ? 5 : -5) : vy;
      const deltaRatioX = dx / safeVx;
      const deltaRatioY = dy / safeVy;

      let scaleFactor;
      if (Math.abs(deltaRatioX) >= Math.abs(deltaRatioY)) {
        scaleFactor = 1 + deltaRatioX;
      } else {
        scaleFactor = 1 + deltaRatioY;
      }
      scaleFactor = Math.max(0.2, Math.min(4.0, scaleFactor));

      onTransformGroup(parentRows, parentAreas, {
        scale: scaleFactor,
        cx,
        cy,
        seatGuids: selectedSeatGuids,
        initialSeats,
        initialRows,
        initialAreas,
      });
      return;
    }

    // Update marquee selection preview
    if (isSelecting) {
      setSelectCurrentPos(coords);
      return;
    }

    // Update drawing preview
    if (isDrawing) {
      setDrawCurrentPos(coords);
      return;
    }

    // Update polygon edge preview
    if (activeTool === 'add-polygon' && polygonDraftPoints.length > 0) {
      setDrawCurrentPos(coords);
    }

    // Dragging objects
    if (isRotatingGroup && selectedGroupBoundingBox && onTransformGroup) {
      const center = groupRotateCenter || { x: selectedGroupBoundingBox.cx, y: selectedGroupBoundingBox.cy };
      const angle1 = Math.atan2(dragStartPos.y - center.y, dragStartPos.x - center.x) * (180 / Math.PI);
      const angle2 = Math.atan2(coords.y - center.y, coords.x - center.x) * (180 / Math.PI);
      const deltaAngle = angle2 - lastRotateAngle;

      if (Math.abs(deltaAngle) > 0.1) {
        onTransformGroup(selectedGroupBoundingBox.parentRows, selectedGroupBoundingBox.parentAreas, {
          angle: deltaAngle,
          cx: center.x,
          cy: center.y
        });
        setLastRotateAngle(angle2);
        setGroupRotateTotalAngle(prev => prev + deltaAngle);
      }
      return;
    }

    if (isDragging && !isZoneLocked) {
      if (!dragOriginRef.current) {
        dragOriginRef.current = dragStartPos;
        dragAppliedDeltaRef.current = { dx: 0, dy: 0 };
      }

      const totalRawDx = coords.x - dragOriginRef.current.x;
      const totalRawDy = coords.y - dragOriginRef.current.y;

      let targetTotalDx = totalRawDx;
      let targetTotalDy = totalRawDy;

      // Photoshop-style Shift constraint: snaps movement to 8 directions (0°, 45°, 90°, 135°, 180°, 225°, 270°, 315°) from the initial position
      if (e.shiftKey) {
        const rad = Math.atan2(totalRawDy, totalRawDx);
        // Snaps to nearest 45 degrees (PI / 4)
        const octant = Math.round((rad * 8) / (2 * Math.PI));
        const snappedAngle = (octant * Math.PI) / 4;

        // Project mouse vector onto the snapped ray
        const proj = totalRawDx * Math.cos(snappedAngle) + totalRawDy * Math.sin(snappedAngle);
        targetTotalDx = proj * Math.cos(snappedAngle);
        targetTotalDy = proj * Math.sin(snappedAngle);
      }

      if (snapToGrid) {
        targetTotalDx = Math.round(targetTotalDx / 10) * 10;
        targetTotalDy = Math.round(targetTotalDy / 10) * 10;
      }

      const dx = Math.round((targetTotalDx - dragAppliedDeltaRef.current.dx) * 10) / 10;
      const dy = Math.round((targetTotalDy - dragAppliedDeltaRef.current.dy) * 10) / 10;

      if (dx !== 0 || dy !== 0) {
        const hasRow = selectedRowIndex !== null;

        // Gather all unique parent row indices
        const parentRows = Array.from(
          new Set([
            ...(hasRow ? [selectedRowIndex] : []),
            ...(rows || []).flatMap((r, i) =>
              (r.seats || []).some((s) => selectedSeatGuids.includes(s.seat_guid))
                ? [i]
                : [],
            ),
          ]),
        );

        // Update groupRotateCenter if moving
        if (groupRotateCenter) {
          setGroupRotateCenter({ x: groupRotateCenter.x + dx, y: groupRotateCenter.y + dy });
        } else if (selectedGroupBoundingBox) {
          setGroupRotateCenter({ x: selectedGroupBoundingBox.cx + dx, y: selectedGroupBoundingBox.cy + dy });
        }

        // Filter out Stage from movable areas when seat rows are moving
        // The Stage must NOT be included when selecting/moving seat rows unless explicitly selected on its own
        const movableAreaIndices = parentRows.length > 0
          ? selectedAreaIndices.filter((idx) => !isStageArea(areas[idx]))
          : selectedAreaIndices;

        if (parentRows.length > 0) {
          // In S mode (seat-select), we NEVER move whole rows; we only move the selected seats.
          // In V mode, whole rows are moved ONLY if all seats of every parent row are selected.
          const isEntireRowSelected =
            activeTool !== 'seat-select' &&
            (hasRow ||
              (parentRows.length > 0 &&
                parentRows.every((rIdx) => {
                  const rSeats = rows[rIdx]?.seats || [];
                  return rSeats.length > 0 && rSeats.every((s) => selectedSeatGuids.includes(s.seat_guid));
                })));

          if (isEntireRowSelected) {
            // Rigid translation of rows: row.position is moved by dx/dy.
            // Individual seat coordinates within each row are completely untouched,
            // preserving 100% of the spacing, distance, width, height, and scale!
            if (onTransformGroup) {
              onTransformGroup(parentRows, movableAreaIndices, { dx, dy });
            } else if (parentRows.length === 1) {
              onMoveRow(parentRows[0], dx, dy);
              if (movableAreaIndices.length > 0) {
                movableAreaIndices.forEach((aIdx) => onMoveArea(aIdx, dx, dy));
              }
            }
          } else {
            // A subset of seats in a single row is selected:
            // Translate those seats rigidly via onMoveMultipleSeats / onMoveSingleSeat
            if (selectedSeatGuids.length === 1) {
              onMoveSingleSeat(selectedSeatGuids[0], dx, dy);
            } else {
              onMoveMultipleSeats(selectedSeatGuids, dx, dy);
            }
            if (movableAreaIndices.length > 0) {
              movableAreaIndices.forEach((aIdx) => onMoveArea(aIdx, dx, dy));
            }
          }
          dragAppliedDeltaRef.current = { dx: targetTotalDx, dy: targetTotalDy };
          setDragStartPos({ x: coords.x, y: coords.y });
        } else if (movableAreaIndices.length > 0) {
          // Only shapes/areas are selected (e.g. Stage explicitly clicked)
          movableAreaIndices.forEach((areaIdx) => {
            onMoveArea(areaIdx, dx, dy);
          });
          dragAppliedDeltaRef.current = { dx: targetTotalDx, dy: targetTotalDy };
          setDragStartPos({ x: coords.x, y: coords.y });
        }
      }
    }
  };

  // Mouse Up handler
  const handleMouseUp = () => {
    setIsPanning(false);
    setIsDragging(false);
    dragOriginRef.current = null;
    dragAppliedDeltaRef.current = { dx: 0, dy: 0 };
    setIsRotatingGroup(false);
    if (isResizingGroup) {
      setIsResizingGroup(false);
      setResizeInitialData(null);
    }
    if (onDragEnd) {
      onDragEnd();
    }

    if (isSelecting) {
      setIsSelecting(false);
      const x1 = selectStartPos.x;
      const y1 = selectStartPos.y;
      const x2 = selectCurrentPos.x;
      const y2 = selectCurrentPos.y;
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);

      const dist = Math.hypot(x2 - x1, y2 - y1);
      if (dist < 6) {
        // Simple click on empty canvas background: clear selection
        onSelectRow(null);
        onSelectSeats([]);
        onSelectAreas([]);
        return;
      }

      // Box selection: find all seats whose canvas coordinate falls inside the box
      const matchedSeatGuids = [];
      const matchedRowIndices = new Set();
      const matchedAreaIndices = [];

      rows.forEach((row, rIdx) => {
        const rx = row.position?.x || 0;
        const ry = row.position?.y || 0;
        const rotDeg = row.rotation || 0;
        const rotRad = (rotDeg * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);

        (row.seats || []).forEach((seat) => {
          const sx = seat.position?.x || 0;
          const sy = seat.position?.y || 0;
          const gx = rx + sx * cosR - sy * sinR;
          const gy = ry + sx * sinR + sy * cosR;

          if (gx >= minX && gx <= maxX && gy >= minY && gy <= maxY) {
            matchedSeatGuids.push(seat.seat_guid);
            matchedRowIndices.add(rIdx);
          }
        });
      });

      if (activeTool !== 'seat-select') {
        matchedRowIndices.forEach(rIdx => {
          const rowGuids = (rows[rIdx]?.seats || []).map(s => s.seat_guid);
          rowGuids.forEach(g => {
            if (!matchedSeatGuids.includes(g)) matchedSeatGuids.push(g);
          });
        });
      }

      // Box selection for areas/shapes
      areas.forEach((area, aIdx) => {
        // The Stage must NEVER be included in marquee/box drag selection!
        // It can only be selected when explicitly clicked.
        if (isStageArea(area)) {
          return;
        }

        const ax = area.position?.x || 0;
        const ay = area.position?.y || 0;
        const rotDeg = area.rotation || 0;
        const rotRad = (rotDeg * Math.PI) / 180;
        // eslint-disable-next-line no-unused-vars
        const cosR = Math.cos(rotRad);
        // eslint-disable-next-line no-unused-vars
        const sinR = Math.sin(rotRad);

        let areaIntersects = false;
        if (area.shape === 'rectangle') {
          const w = area.rectangle?.width || 0;
          const h = area.rectangle?.height || 0;
          // Check if rectangle intersects with selection box
          const rectMinX = ax;
          const rectMaxX = ax + w;
          const rectMinY = ay;
          const rectMaxY = ay + h;
          areaIntersects = !(
            rectMaxX < minX ||
            rectMinX > maxX ||
            rectMaxY < minY ||
            rectMinY > maxY
          );
        } else if (area.shape === 'circle') {
          // eslint-disable-next-line no-unused-vars
          const r = area.circle?.radius || 0;
          // Simple check: if circle center is inside selection box
          areaIntersects = ax >= minX && ax <= maxX && ay >= minY && ay <= maxY;
        } else if (area.shape === 'ellipse') {
          // eslint-disable-next-line no-unused-vars
          const rx = area.ellipse?.radius?.x || 0;
          // eslint-disable-next-line no-unused-vars
          const ry = area.ellipse?.radius?.y || 0;
          // Simple check: if ellipse center is inside selection box
          areaIntersects = ax >= minX && ax <= maxX && ay >= minY && ay <= maxY;
        } else if (area.shape === 'polygon') {
          // Simple check: if polygon position is inside selection box
          areaIntersects = ax >= minX && ax <= maxX && ay >= minY && ay <= maxY;
        } else if (area.shape === 'text') {
          // Simple check: if text position is inside selection box
          areaIntersects = ax >= minX && ax <= maxX && ay >= minY && ay <= maxY;
        }

        if (areaIntersects) {
          matchedAreaIndices.push(aIdx);
        }
      });

      if (matchedSeatGuids.length > 0) {
        // Seat rows were selected via marquee box.
        // Stage and other shapes must NEVER be included when selecting seat rows!
        if (activeTool === 'select' && matchedRowIndices.size === 1) {
          const [singleRowIdx] = Array.from(matchedRowIndices);
          onSelectRow(singleRowIdx);
        } else {
          onSelectRow(null);
        }
        onSelectSeats(matchedSeatGuids);
        onSelectAreas([]);
        return;
      }

      if (matchedAreaIndices.length > 0) {
        onSelectRow(null);
        onSelectSeats([]);
        onSelectAreas(matchedAreaIndices);
        return;
      }

      onSelectRow(null);
      onSelectSeats([]);
      onSelectAreas([]);
      return;
    }

    if (isDrawing) {
      setIsDrawing(false);
      const x1 = drawStartPos.x;
      const y1 = drawStartPos.y;
      const x2 = drawCurrentPos.x;
      const y2 = drawCurrentPos.y;

      const minX = Math.min(x1, x2);
      const minY = Math.min(y1, y2);
      const dragW = Math.max(30, Math.abs(x2 - x1));
      const dragH = Math.max(30, Math.abs(y2 - y1));

      if (activeTool === 'add-rows') {
        // 2D Grid Creation
        const cols = Math.max(2, Math.round(dragW / 26));
        const numRows = Math.max(2, Math.round(dragH / 30));
        onCommitCreatedRows({
          startX: minX,
          startY: minY,
          rowCount: numRows,
          seatsPerRow: cols,
          seatSpacingX: 25,
          rowSpacingY: 28,
        });
      } else if (activeTool === 'add-row') {
        // Angled 1D Row Creation
        const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        const angleDeg = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
        const seatCount = Math.max(2, Math.round(dist / 25));
        onCommitCreatedRow({
          startX: x1,
          startY: y1,
          seatCount,
          seatSpacing: 25,
          rotation: Math.round(angleDeg),
        });
      } else if (activeTool === 'add-rect') {
        onCommitCreatedArea({
          shape: 'rectangle',
          uuid: Math.random().toString(36).substring(2, 9),
          position: { x: minX + dragW / 2, y: minY + dragH / 2 },
          rotation: 0,
          color: '#CCCCCC',
          border_color: '#000001',
          rectangle: { width: dragW, height: dragH },
          text: {
            text: '',
            color: '#333333',
            position: { x: 0, y: 5 },
          },
        });
      } else if (activeTool === 'add-circle') {
        const radius = Math.max(
          15,
          Math.round(Math.hypot(x2 - x1, y2 - y1) / 2),
        );
        const centerX = (x1 + x2) / 2;
        const centerY = (y1 + y2) / 2;
        onCommitCreatedArea({
          shape: 'circle',
          uuid: Math.random().toString(36).substring(2, 9),
          position: { x: centerX, y: centerY },
          rotation: 0,
          color: '#CCCCCC',
          border_color: '#000001',
          circle: { radius },
          text: { text: '', color: '#333333', position: { x: 0, y: 5 } },
        });
      } else if (activeTool === 'add-ellipse') {
        const rx = Math.max(15, Math.round(dragW / 2));
        const ry = Math.max(15, Math.round(dragH / 2));
        onCommitCreatedArea({
          shape: 'ellipse',
          uuid: Math.random().toString(36).substring(2, 9),
          position: { x: minX + rx, y: minY + ry },
          rotation: 0,
          color: '#CCCCCC',
          border_color: '#000001',
          ellipse: { radius: { x: rx, y: ry } },
          text: { text: '', color: '#333333', position: { x: 0, y: 5 } },
        });
      }

      if (onFinishDrawing) onFinishDrawing();
    }
  };

  // Click handler for seats
  const handleSeatClick = (e, rowIdx, seatGuid) => {
    e.stopPropagation();
    if (isZoneLocked) return;

    const coords = getCanvasCoords(e.clientX, e.clientY);

    if (e.shiftKey) {
      if (activeTool === 'seat-select') {
        // S Mode: Toggle single seat
        if (selectedSeatGuids.includes(seatGuid)) {
          const newSelection = selectedSeatGuids.filter((g) => g !== seatGuid);
          onSelectSeats(newSelection);
          if (newSelection.length === 0) onSelectRow(null);
        } else {
          onSelectSeats([...selectedSeatGuids, seatGuid]);
        }
      } else {
        // V Mode: Toggle entire row
        const targetRow = rows[rowIdx];
        const rowGuids = (targetRow?.seats || []).map((s) => s.seat_guid);
        const allInSelection = rowGuids.every(g => selectedSeatGuids.includes(g));

        if (allInSelection) {
          // Remove entire row from selection
          onSelectSeats(selectedSeatGuids.filter(g => !rowGuids.includes(g)));
          if (selectedRowIndex === rowIdx) onSelectRow(null);
        } else {
          // Add entire row to selection
          onSelectSeats(Array.from(new Set([...selectedSeatGuids, ...rowGuids])));
          onSelectRow(rowIdx);
        }
      }
      onSelectAreas([]);
    } else if (activeTool === 'seat-select') {
      // S Mode: Select ONLY this single seat
      if (!selectedSeatGuids.includes(seatGuid)) {
        onSelectRow(null);
        onSelectSeats([seatGuid]);
        onSelectAreas([]);
      }
    } else {
      // V Mode: Select the ENTIRE row group
      const targetRow = rows[rowIdx];
      const rowGuids = (targetRow?.seats || []).map((s) => s.seat_guid);
      const isAlreadySelected = rowGuids.every(g => selectedSeatGuids.includes(g));

      if (!isAlreadySelected) {
        onSelectRow(rowIdx);
        onSelectSeats(rowGuids);
        onSelectAreas([]);
      } else {
        onSelectAreas([]);
      }
    }

    setIsDragging(true);
    setDragStartPos(coords);
    dragOriginRef.current = coords;
    dragAppliedDeltaRef.current = { dx: 0, dy: 0 };
  };

  // Click handler for area/shapes
  const handleAreaClick = (e, areaIdx) => {
    e.stopPropagation();
    if (isZoneLocked) return;

    const coords = getCanvasCoords(e.clientX, e.clientY);

    if (e.shiftKey) {
      // Shift+Click: Toggle area in selection
      if (
        Array.isArray(selectedAreaIndices) &&
        selectedAreaIndices.includes(areaIdx)
      ) {
        // Remove area from selection
        const newSelection = selectedAreaIndices.filter(
          (idx) => idx !== areaIdx,
        );
        onSelectAreas(newSelection);
      } else {
        // Add area to selection
        onSelectAreas([
          ...(Array.isArray(selectedAreaIndices) ? selectedAreaIndices : []),
          areaIdx,
        ]);
      }
    } else {
      // Normal click: Select area
      if (
        !Array.isArray(selectedAreaIndices) ||
        !selectedAreaIndices.includes(areaIdx)
      ) {
        onSelectRow(null);
        onSelectSeats([]);
        onSelectAreas([areaIdx]);
      }
    }

    setIsDragging(true);
    setDragStartPos(coords);
    dragOriginRef.current = coords;
    dragAppliedDeltaRef.current = { dx: 0, dy: 0 };
  };

  const selectedSet = React.useMemo(
    () => new Set(selectedSeatGuids),
    [selectedSeatGuids],
  );

  // Compute tight bounding box of entire plan for 100% vector Print / PDF export
  const printBounds = React.useMemo(() => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    const addPt = (px, py) => {
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    };

    (rows || []).forEach((r) => {
      const rx = r.position?.x || 0;
      const ry = r.position?.y || 0;
      const rRad = ((r.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(rRad);
      const sin = Math.sin(rRad);

      (r.seats || []).forEach((s) => {
        const lx = s.position?.x || 0;
        const ly = s.position?.y || 0;
        const rad = s.radius || r.seat_radius || 10;
        const absX = rx + (lx * cos - ly * sin);
        const absY = ry + (lx * sin + ly * cos);
        addPt(absX - rad, absY - rad);
        addPt(absX + rad, absY + rad);
      });
      if (!r.seats || r.seats.length === 0) addPt(rx, ry);
    });

    (areas || []).forEach((a) => {
      const ax = a.position?.x || 0;
      const ay = a.position?.y || 0;
      const aRad = ((a.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(aRad);
      const sin = Math.sin(aRad);

      if (a.shape === 'rectangle' && a.rectangle) {
        const w = a.rectangle.width || 60;
        const h = a.rectangle.height || 60;
        [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]].forEach(([dx, dy]) => {
          addPt(ax + dx * cos - dy * sin, ay + dx * sin + dy * cos);
        });
      } else if (a.shape === 'circle' && a.circle) {
        const rad = a.circle?.radius || 30;
        addPt(ax - rad, ay - rad);
        addPt(ax + rad, ay + rad);
      } else if (a.shape === 'ellipse' && a.ellipse) {
        const rx = a.ellipse?.radius?.x || 50;
        const ry = a.ellipse?.radius?.y || 80;
        [[-rx, -ry], [rx, -ry], [rx, ry], [-rx, ry]].forEach(([dx, dy]) => {
          addPt(ax + dx * cos - dy * sin, ay + dx * sin + dy * cos);
        });
      } else if (a.shape === 'text' && a.text) {
        const fs = a.text?.fontSize || 16;
        const tw = Math.max(40, (a.text?.text?.length || 4) * (fs * 0.65));
        const th = fs * 1.3;
        [[-tw / 2, -th / 2], [tw / 2, -th / 2], [tw / 2, th / 2], [-tw / 2, th / 2]].forEach(([dx, dy]) => {
          addPt(ax + dx * cos - dy * sin, ay + dx * sin + dy * cos);
        });
      } else if (a.shape === 'polygon' && a.polygon?.points) {
        (a.polygon.points || []).forEach((p) => {
          addPt(ax + p.x * cos - p.y * sin, ay + p.x * sin + p.y * cos);
        });
      } else {
        addPt(ax - 30, ay - 30);
        addPt(ax + 30, ay + 30);
      }
    });

    if (minX === Infinity || maxX === -Infinity) {
      minX = 0;
      maxX = width || 900;
      minY = 0;
      maxY = height || 900;
    }

    const pad = 40;
    return {
      x: Math.round(minX - pad),
      y: Math.round(minY - pad),
      width: Math.max(100, Math.round(maxX - minX + pad * 2)),
      height: Math.max(100, Math.round(maxY - minY + pad * 2)),
    };
  }, [rows, areas, width, height]);

  // Render Area / Shape helper (shared between canvas editor and visual vector print)
  const renderAreaItem = (area, aIdx, isPrint = false) => {
    const isSelected = !isPrint && Array.isArray(selectedAreaIndices) && selectedAreaIndices.includes(aIdx);
    const ax = area.position?.x || 0;
    const ay = area.position?.y || 0;
    const shape = area.shape || 'rectangle';
    const fillColor = area.color || '#CCCCCC';
    const strokeColor = area.border_color || '#000001';
    const strokeWidth = 1.5;
    const dashArray = 'none';

    return (
      <g
        key={area.uuid || aIdx}
        transform={`translate(${ax}, ${ay}) rotate(${area.rotation || 0})`}
        opacity={area.opacity ?? 1.0}
        cursor={isPrint || isZoneLocked ? 'default' : 'move'}
        onMouseDown={isPrint ? undefined : (e) => handleAreaClick(e, aIdx)}
      >
        {shape === 'rectangle' && (
          <rect
            x={-(area.rectangle?.width || 120) / 2}
            y={-(area.rectangle?.height || 60) / 2}
            width={area.rectangle?.width || 120}
            height={area.rectangle?.height || 60}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
            rx="4"
          />
        )}

        {shape === 'circle' && (
          <circle
            cx={0}
            cy={0}
            r={area.circle?.radius || 50}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
          />
        )}

        {shape === 'ellipse' && (
          <ellipse
            cx={0}
            cy={0}
            rx={area.ellipse?.radius?.x || 50}
            ry={area.ellipse?.radius?.y || 80}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
          />
        )}

        {shape === 'polygon' && (
          <polygon
            points={
              area.polygon?.points
                ?.map((p) => `${p.x},${p.y}`)
                .join(' ') || ''
            }
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
          />
        )}

        {shape === 'text' && (
          <>
            {!isPrint && (
              <rect
                x={-(Math.max(40, (area.text?.text?.length || 4) * ((area.text?.fontSize || 16) * 0.65) + 12)) / 2}
                y={-((area.text?.fontSize || 16) * 1.3 + 8) / 2}
                width={Math.max(40, (area.text?.text?.length || 4) * ((area.text?.fontSize || 16) * 0.65) + 12)}
                height={(area.text?.fontSize || 16) * 1.3 + 8}
                fill="transparent"
                stroke="transparent"
                cursor="pointer"
              />
            )}
            <text
              x={0}
              y={0}
              textAnchor="middle"
              dominantBaseline="central"
              fill={area.text?.color || '#333333'}
              fontSize={`${area.text?.fontSize || 16}px`}
              fontWeight="700"
              cursor={isPrint ? 'default' : 'pointer'}
            >
              {area.text?.text || 'Text'}
            </text>
          </>
        )}

        {shape !== 'text' && area.text?.text && (
          <text
            x={0}
            y={0}
            textAnchor="middle"
            dominantBaseline="central"
            fill={area.text.color || '#333333'}
            fontSize={`${area.text.fontSize || 16}px`}
            fontWeight="700"
            style={{ pointerEvents: 'none' }}
          >
            {area.text.text}
          </text>
        )}
      </g>
    );
  };

  // Render Row and Seats helper (shared between canvas editor and visual vector print)
  const renderRowItem = (row, rIdx, isPrint = false) => {
    const rx = row.position?.x || 0;
    const ry = row.position?.y || 0;
    const seats = row.seats || [];
    const isRowSelected = !isPrint && selectedRowIndex === rIdx;
    const showNumbers = Boolean(row.show_row_numbers);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    seats.forEach((s) => {
      const sx = s.position?.x || 0;
      const sy = s.position?.y || 0;
      const r = s.radius || row.seat_radius || 10;
      if (sx - r < minX) minX = sx - r;
      if (sx + r > maxX) maxX = sx + r;
      if (sy - r < minY) minY = sy - r;
      if (sy + r > maxY) maxY = sy + r;
    });
    if (minX === Infinity) {
      minX = 0; maxX = 0; minY = 0; maxY = 0;
    }

    return (
      <g
        key={`row-${rIdx}-${row.uuid || row.row_number}${isPrint ? '-print' : ''}`}
        transform={`translate(${rx}, ${ry}) rotate(${row.rotation || 0})`}
      >
        {/* Row Drag Hit Area (editor only) */}
        {!isPrint && isRowSelected && seats.length > 0 && (
          <rect
            x={minX - 12}
            y={minY - 12}
            width={maxX - minX + 24}
            height={maxY - minY + 24}
            fill="transparent"
            stroke="none"
            cursor={isZoneLocked ? 'not-allowed' : 'move'}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (isZoneLocked) return;
              const coords = getCanvasCoords(e.clientX, e.clientY);
              setIsDragging(true);
              setDragStartPos(coords);
              dragOriginRef.current = coords;
              dragAppliedDeltaRef.current = { dx: 0, dy: 0 };
            }}
          />
        )}

        {/* Row Number labels placed accurately next to first and/or last seats (counter-rotated so upright) */}
        {showNumbers && seats.length > 0 && calculateRowLabelPositions(row).map((lbl, lIdx) => (
          <text
            key={`row-lbl-${rIdx}-${lbl.side}-${lIdx}${isPrint ? '-print' : ''}`}
            x={lbl.x}
            y={lbl.y}
            transform={row.rotation ? `rotate(${-row.rotation}, ${lbl.x}, ${lbl.y})` : undefined}
            textAnchor={lbl.textAnchor}
            dominantBaseline={lbl.dominantBaseline}
            fill={!isPrint && isRowSelected ? '#60A5FA' : '#94A3B8'}
            fontSize="11px"
            fontWeight="700"
            cursor={isPrint || isZoneLocked ? 'default' : 'pointer'}
            onMouseDown={isPrint ? undefined : (e) => {
              e.stopPropagation();
              if (isZoneLocked) return;
              const rowGuids = (row.seats || []).map((s) => s.seat_guid);
              if (e.shiftKey) {
                const allInSelection = rowGuids.length > 0 && rowGuids.every((g) => selectedSeatGuids.includes(g));
                if (allInSelection) {
                  const newGuids = selectedSeatGuids.filter((g) => !rowGuids.includes(g));
                  onSelectSeats(newGuids);
                  if (selectedRowIndex === rIdx) onSelectRow(null);
                } else {
                  onSelectSeats(Array.from(new Set([...selectedSeatGuids, ...rowGuids])));
                }
                onSelectAreas([]);
              } else {
                onSelectRow(rIdx);
                onSelectSeats(rowGuids);
                onSelectAreas([]);
              }
              const coords = getCanvasCoords(e.clientX, e.clientY);
              setIsDragging(true);
              setDragStartPos(coords);
              dragOriginRef.current = coords;
              dragAppliedDeltaRef.current = { dx: 0, dy: 0 };
            }}
          >
            {lbl.text}
          </text>
        ))}

        {/* Seats in this Row */}
        {seats.map((seat) => {
          const sx = seat.position?.x || 0;
          const sy = seat.position?.y || 0;
          const isSeatSelected = !isPrint && selectedSet.has(seat.seat_guid);
          const seatRadius = seat.radius || row.seat_radius || 10;
          const netSeatAngle = ((row.rotation || 0) + (seat.rotation || 0)) % 360;
          let seatColor =
            seat.category && categoryColorMap[seat.category]
              ? categoryColorMap[seat.category]
              : '#FFFFFF';

          const isLightSeat =
            !seat.category ||
            seatColor.toUpperCase() === '#FFFFFF' ||
            seatColor.toUpperCase() === '#FFF' ||
            seatColor.toUpperCase() === '#C0C0C0';
          const textColor = isLightSeat ? '#0F172A' : '#FFFFFF';
          const subTextColor = isLightSeat
            ? '#334155'
            : 'rgba(255,255,255,0.9)';
          const optHint = seat.optimizer_hint || row.optimizer_hint;

          const clipId = `seat-clip-${seat.seat_guid || seat.uuid}${isPrint ? '-print' : ''}`;
          return (
            <g
              key={seat.seat_guid || seat.uuid}
              transform={`translate(${sx}, ${sy}) rotate(${seat.rotation || 0})`}
              cursor={isPrint || isZoneLocked ? 'default' : 'pointer'}
              onMouseDown={isPrint ? undefined : (e) =>
                handleSeatClick(e, rIdx, seat.seat_guid)
              }
            >
              {/* ClipPath so label never overflows circle */}
              <defs>
                <clipPath id={clipId}>
                  <circle r={seatRadius - 1} />
                </clipPath>
              </defs>

              {/* Halo ring for selected seat (editor only) */}
              {isSeatSelected && (
                <circle
                  r={seatRadius + 4}
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="2.5"
                  strokeDasharray="4,2"
                />
              )}

              {/* Main seat circle */}
              <circle
                r={seatRadius}
                fill={seatColor}
                stroke={
                  isSeatSelected
                    ? '#2563EB'
                    : isLightSeat
                      ? '#94A3B8'
                      : 'rgba(0,0,0,0.3)'
                }
                strokeWidth={isSeatSelected ? 2 : 1.2}
              />

              {/* Clipped content group — counter-rotate so text is ALWAYS upright and horizontally readable */}
              <g
                clipPath={`url(#${clipId})`}
                transform={netSeatAngle !== 0 ? `rotate(${-netSeatAngle})` : undefined}
              >
                {/* Seat Number Text */}
                <text
                  y={
                    optHint
                      ? 2
                      : row.seat_label
                        ? -seatRadius * 0.22
                        : 3.5
                  }
                  textAnchor="middle"
                  fill={textColor}
                  fontSize={`${Math.max(6, seatRadius * 0.65)}px`}
                  fontWeight="700"
                  style={{ pointerEvents: 'none' }}
                >
                  {seat.seat_number}
                </text>

                {/* Optimizer hint arrow indicator */}
                {optHint && (
                  <text
                    y={-seatRadius * 0.42}
                    textAnchor="middle"
                    fill={textColor}
                    fontSize={`${Math.max(5, seatRadius * 0.5)}px`}
                    fontWeight="900"
                    style={{ pointerEvents: 'none' }}
                  >
                    {optHint === '<'
                      ? '◀'
                      : optHint === '>'
                        ? '▶'
                        : '◀▶'}
                  </text>
                )}

                {/* Seat Label (e.g. VIP) — clipped inside circle */}
                {row.seat_label && (
                  <text
                    y={seatRadius * 0.52}
                    textAnchor="middle"
                    fill={subTextColor}
                    fontSize={`${Math.max(4, Math.min(seatRadius * 0.42, ((seatRadius * 2 * 0.85) / Math.max(1, row.seat_label.length)) * 1.5))}px`}
                    fontWeight="600"
                    style={{ pointerEvents: 'none' }}
                  >
                    {row.seat_label}
                  </text>
                )}
              </g>
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <Box
      ref={containerRef}
      w="100%"
      h="100%"
      overflow="hidden"
      position="relative"
      bg={canvasBg}
      userSelect="none"
      cursor={
        isPanning
          ? 'grabbing'
          : [
            'add-rows',
            'add-row',
            'add-rect',
            'add-circle',
            'add-ellipse',
            'add-polygon',
          ].includes(activeTool)
            ? 'crosshair'
            : activeTool === 'add-seat' || activeTool === 'add-text'
              ? 'cell'
              : 'default'
      }
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <svg
        ref={svgRef}
        id="seating-plan-canvas-svg"
        width="100%"
        height="100%"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <defs>
          {/* Dot grid (snap OFF) */}
          <pattern
            id="editor-grid-dots"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="10" cy="10" r="1.2" fill={gridColor} />
          </pattern>
          {/* Line grid (snap ON) */}
          <pattern
            id="editor-grid-lines"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke={gridColor}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>

        <rect
          id="canvas-bg"
          x={0}
          y={0}
          width="100%"
          height="100%"
          fill={canvasBg}
        />

        {/* Scaled & Translated Viewport */}
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Plan Boundary with adaptive grid pattern */}
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill={
              snapToGrid ? 'url(#editor-grid-lines)' : 'url(#editor-grid-dots)'
            }
            stroke={planBorderColor}
            strokeWidth="1.5"
            style={{ pointerEvents: 'none' }}
          />

          {/* Floorplan Background Blueprint Image */}
          {plan?.background_image_url && (
            <image
              href={plan.background_image_url}
              x={plan.background_settings?.x || 0}
              y={plan.background_settings?.y || 0}
              width={plan.background_settings?.width || width}
              height={plan.background_settings?.height || height}
              opacity={plan.background_settings?.opacity ?? 0.8}
              style={{ pointerEvents: 'none' }}
            />
          )}

          {/* Render Areas / Shapes (Rectangle, Circle, Ellipse, Polygon, Text) */}
          {areas.map((area, aIdx) => renderAreaItem(area, aIdx, false))}

          {/* Render Rows and Seats */}
          {rows.map((row, rIdx) => renderRowItem(row, rIdx, false))}

          {/* ---- add-rows [B]: ghost grid preview ---- */}
          {isDrawing &&
            activeTool === 'add-rows' &&
            (() => {
              const x1 = Math.min(drawStartPos.x, drawCurrentPos.x);
              const y1 = Math.min(drawStartPos.y, drawCurrentPos.y);
              const w = Math.abs(drawCurrentPos.x - drawStartPos.x);
              const h = Math.abs(drawCurrentPos.y - drawStartPos.y);
              const SEAT_R = 10;
              const SPACING = 25;
              const ROW_GAP = 30;
              const cols = Math.max(1, Math.round(w / SPACING));
              const rows2 = Math.max(1, Math.round(h / ROW_GAP));
              const dots = [];
              for (let r = 0; r < rows2; r++) {
                for (let c = 0; c < cols; c++) {
                  dots.push({ x: x1 + c * SPACING, y: y1 + r * ROW_GAP });
                }
              }
              return (
                <g>
                  {/* boundary */}
                  <rect
                    x={x1}
                    y={y1}
                    width={w}
                    height={h}
                    fill="rgba(59,130,246,0.07)"
                    stroke="#3B82F6"
                    strokeWidth="1.5"
                    strokeDasharray="5,3"
                  />
                  {/* ghost seats */}
                  {dots.map((d, i) => (
                    <circle
                      key={i}
                      cx={d.x}
                      cy={d.y}
                      r={SEAT_R}
                      fill="rgba(59,130,246,0.35)"
                      stroke="#3B82F6"
                      strokeWidth="1"
                    />
                  ))}
                  {/* counter label */}
                  <text
                    x={x1 + w / 2}
                    y={y1 - 8}
                    textAnchor="middle"
                    fill="#3B82F6"
                    fontSize="11px"
                    fontWeight="700"
                  >
                    {rows2} row{rows2 !== 1 ? 's' : ''} × {cols} seat
                    {cols !== 1 ? 's' : ''} = {rows2 * cols}
                  </text>
                </g>
              );
            })()}

          {/* ---- add-row [N]: ghost angled seat dots preview ---- */}
          {isDrawing &&
            activeTool === 'add-row' &&
            (() => {
              const dx = drawCurrentPos.x - drawStartPos.x;
              const dy = drawCurrentPos.y - drawStartPos.y;
              const dist = Math.hypot(dx, dy);
              const SPACING = 25;
              const SEAT_R = 10;
              const count = Math.max(1, Math.round(dist / SPACING));
              const ux = dist > 0 ? dx / dist : 1;
              const uy = dist > 0 ? dy / dist : 0;
              return (
                <g>
                  {/* guide line */}
                  <line
                    x1={drawStartPos.x}
                    y1={drawStartPos.y}
                    x2={drawCurrentPos.x}
                    y2={drawCurrentPos.y}
                    stroke="#3B82F6"
                    strokeWidth="1.5"
                    strokeDasharray="6,3"
                  />
                  {/* ghost seats */}
                  {Array.from({ length: count }, (_, i) => ({
                    x: drawStartPos.x + i * SPACING * ux,
                    y: drawStartPos.y + i * SPACING * uy,
                  })).map((d, i) => (
                    <circle
                      key={i}
                      cx={d.x}
                      cy={d.y}
                      r={SEAT_R}
                      fill="rgba(59,130,246,0.35)"
                      stroke="#3B82F6"
                      strokeWidth="1"
                    />
                  ))}
                  {/* counter label */}
                  <text
                    x={(drawStartPos.x + drawCurrentPos.x) / 2}
                    y={(drawStartPos.y + drawCurrentPos.y) / 2 - 14}
                    textAnchor="middle"
                    fill="#3B82F6"
                    fontSize="11px"
                    fontWeight="700"
                  >
                    {count} seat{count !== 1 ? 's' : ''}
                  </text>
                </g>
              );
            })()}

          {isDrawing && activeTool === 'add-rect' && (
            <rect
              x={Math.min(drawStartPos.x, drawCurrentPos.x)}
              y={Math.min(drawStartPos.y, drawCurrentPos.y)}
              width={Math.abs(drawCurrentPos.x - drawStartPos.x)}
              height={Math.abs(drawCurrentPos.y - drawStartPos.y)}
              fill="rgba(203, 213, 225, 0.5)"
              stroke="#3B82F6"
              strokeWidth="2"
            />
          )}

          {isDrawing && activeTool === 'add-circle' && (
            <circle
              cx={(drawStartPos.x + drawCurrentPos.x) / 2}
              cy={(drawStartPos.y + drawCurrentPos.y) / 2}
              r={
                Math.hypot(
                  drawCurrentPos.x - drawStartPos.x,
                  drawCurrentPos.y - drawStartPos.y,
                ) / 2
              }
              fill="rgba(203, 213, 225, 0.5)"
              stroke="#3B82F6"
              strokeWidth="2"
            />
          )}

          {isDrawing && activeTool === 'add-ellipse' && (
            <ellipse
              cx={(drawStartPos.x + drawCurrentPos.x) / 2}
              cy={(drawStartPos.y + drawCurrentPos.y) / 2}
              rx={Math.abs(drawCurrentPos.x - drawStartPos.x) / 2}
              ry={Math.abs(drawCurrentPos.y - drawStartPos.y) / 2}
              fill="rgba(203, 213, 225, 0.5)"
              stroke="#3B82F6"
              strokeWidth="2"
            />
          )}

          {/* Polygon Vertices Preview */}
          {activeTool === 'add-polygon' && polygonDraftPoints.length > 0 && (
            <g>
              <polyline
                points={polygonDraftPoints
                  .map((pt) => `${pt.x},${pt.y}`)
                  .join(' ')}
                fill="none"
                stroke="#3B82F6"
                strokeWidth="2"
              />
              <line
                x1={polygonDraftPoints[polygonDraftPoints.length - 1].x}
                y1={polygonDraftPoints[polygonDraftPoints.length - 1].y}
                x2={drawCurrentPos.x}
                y2={drawCurrentPos.y}
                stroke="#60A5FA"
                strokeWidth="1.5"
                strokeDasharray="4,2"
              />
              {polygonDraftPoints.map((pt, i) => (
                <circle
                  key={i}
                  cx={pt.x}
                  cy={pt.y}
                  r={4}
                  fill="#3B82F6"
                  stroke="#FFFFFF"
                  strokeWidth={1}
                />
              ))}
            </g>
          )}

          {/* Marquee Selection Rectangle */}
          {isSelecting && (
            <rect
              x={Math.min(selectStartPos.x, selectCurrentPos.x)}
              y={Math.min(selectStartPos.y, selectCurrentPos.y)}
              width={Math.abs(selectCurrentPos.x - selectStartPos.x)}
              height={Math.abs(selectCurrentPos.y - selectStartPos.y)}
              fill="rgba(59, 130, 246, 0.12)"
              stroke="#3B82F6"
              strokeWidth="1.5"
              strokeDasharray="4,3"
              style={{ pointerEvents: 'none' }}
            />
          )}

          {/* Group Transform Bounding Box & Rotation Handle */}
          {selectedGroupBoundingBox && activeTool !== 'seat-select' && !isZoneLocked && (
            <g
              className="canvas-interactive-overlay no-print"
              transform={`rotate(${groupRotateTotalAngle}, ${selectedGroupBoundingBox.cx}, ${selectedGroupBoundingBox.cy})`}
            >
              {/* Main Bounding Box */}
              <rect
                x={selectedGroupBoundingBox.minX}
                y={selectedGroupBoundingBox.minY}
                width={selectedGroupBoundingBox.width}
                height={selectedGroupBoundingBox.height}
                fill="none"
                stroke="#0000cc"
                strokeWidth="1.5"
                strokeDasharray="4,4"
                pointerEvents="none"
              />
              {/* Rotation line from center to handle */}
              <line
                x1={selectedGroupBoundingBox.cx}
                y1={selectedGroupBoundingBox.cy}
                x2={selectedGroupBoundingBox.cx}
                y2={selectedGroupBoundingBox.minY - 15}
                stroke="#0000cc"
                strokeWidth="1.5"
              />
              {/* Center hollow circle */}
              <circle
                cx={selectedGroupBoundingBox.cx}
                cy={selectedGroupBoundingBox.cy}
                r={4}
                fill="#ffffff"
                stroke="#0000cc"
                strokeWidth="1.5"
                pointerEvents="none"
              />
              {/* Rotation Handle */}
              <circle
                cx={selectedGroupBoundingBox.cx}
                cy={selectedGroupBoundingBox.minY - 15}
                r={5}
                fill="#0000cc"
                cursor="crosshair"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const coords = getCanvasCoords(e.clientX, e.clientY);
                  setIsRotatingGroup(true);
                  if (!groupRotateCenter) {
                    setGroupRotateCenter({ x: selectedGroupBoundingBox.cx, y: selectedGroupBoundingBox.cy });
                  }
                  setDragStartPos(coords);
                  setLastRotateAngle(Math.atan2(coords.y - selectedGroupBoundingBox.cy, coords.x - selectedGroupBoundingBox.cx) * (180 / Math.PI));
                }}
              />
              {/* 4 Corner Resize Handles (Photoshop / Figma style) */}
              {[
                { id: 'nw', x: selectedGroupBoundingBox.minX, y: selectedGroupBoundingBox.minY, cursor: 'nwse-resize' },
                { id: 'ne', x: selectedGroupBoundingBox.minX + selectedGroupBoundingBox.width, y: selectedGroupBoundingBox.minY, cursor: 'nesw-resize' },
                { id: 'sw', x: selectedGroupBoundingBox.minX, y: selectedGroupBoundingBox.minY + selectedGroupBoundingBox.height, cursor: 'nesw-resize' },
                { id: 'se', x: selectedGroupBoundingBox.minX + selectedGroupBoundingBox.width, y: selectedGroupBoundingBox.minY + selectedGroupBoundingBox.height, cursor: 'nwse-resize' }
              ].map((h) => (
                <rect
                  key={h.id}
                  x={h.x - 4}
                  y={h.y - 4}
                  width={8}
                  height={8}
                  fill="#ffffff"
                  stroke="#0000cc"
                  strokeWidth={1.5}
                  cursor={h.cursor}
                  onMouseDown={(e) => handleStartResize(e, h)}
                />
              ))}
            </g>
          )}
        </g>
      </svg>

      {/* High-Resolution Vector Print SVG (Fits to page format, centered, single sheet) */}
      <svg
        id="seating-plan-print-svg"
        viewBox={`${printBounds.x} ${printBounds.y} ${printBounds.width} ${printBounds.height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', display: 'none' }}
      >
        <rect
          x={printBounds.x}
          y={printBounds.y}
          width={printBounds.width}
          height={printBounds.height}
          fill="#ffffff"
        />

        {/* Floorplan Background Blueprint Image */}
        {plan?.background_image_url && (
          <image
            href={plan.background_image_url}
            x={plan.background_settings?.x || 0}
            y={plan.background_settings?.y || 0}
            width={plan.background_settings?.width || width}
            height={plan.background_settings?.height || height}
            opacity={plan.background_settings?.opacity ?? 0.8}
          />
        )}

        {/* Areas */}
        {areas.map((area, aIdx) => renderAreaItem(area, aIdx, true))}

        {/* Rows and Seats */}
        {rows.map((row, rIdx) => renderRowItem(row, rIdx, true))}
      </svg>

      {/* Bottom Status Bar */}
      <Flex
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="28px"
        bg="rgba(15, 23, 42, 0.9)"
        backdropFilter="blur(8px)"
        borderTop="1px solid rgba(255, 255, 255, 0.1)"
        align="center"
        px={3}
        color="gray.400"
        fontSize="xs"
        justify="space-between"
        pointerEvents="none"
        zIndex={5}
      >
        <Text>
          {activeTool === 'add-polygon'
            ? 'Click to place vertex • Press ENTER to complete polygon • Press ESC to cancel'
            : [
              'add-rows',
              'add-row',
              'add-rect',
              'add-circle',
              'add-ellipse',
            ].includes(activeTool)
              ? 'Click and drag on canvas to create • Press ESC to cancel'
              : activeTool === 'add-seat'
                ? 'Click anywhere on canvas to drop a single seat'
                : activeTool === 'seat-select'
                  ? 'Mode [S]: Select individual seats • Hold SHIFT to lock 0°/45°/90° axis • Two-finger scroll to pan, Ctrl+scroll to zoom'
                  : 'Mode [V]: Select rows/shapes • Hold SHIFT to lock 0°/45°/90° axis • Two-finger scroll to pan, Ctrl+scroll to zoom'}
        </Text>
        <Text color="gray.400">
          {isZoneLocked ? (
            <span style={{ color: '#F87171' }}>🔒 Zone Locked</span>
          ) : selectedRowIndex !== null ? (
            <span style={{ color: '#60A5FA' }}>
              Selected Row: {rows[selectedRowIndex]?.row_number}
            </span>
          ) : selectedSeatGuids.length > 0 ? (
            <span style={{ color: '#60A5FA' }}>
              Selected: {selectedSeatGuids.length} seat(s)
            </span>
          ) : selectedAreaIndices.length > 0 ? (
            <span style={{ color: '#C084FC' }}>
              Selected: {selectedAreaIndices.length} shape(s)
            </span>
          ) : (
            'No selection'
          )}
        </Text>
      </Flex>
    </Box>
  );
}
